import { supabase } from '@/lib/supabase';

export type MatchedContact = {
  id: string;
  full_name: string | null;
  nickname: string | null;
  avatar_url: string | null;
  verified: boolean;
  phone_verified: boolean;
  current_level: number;
  portfolio_enabled: boolean;
  user_number: number | null;
  connection_status: 'none' | 'pending_sent' | 'pending_received' | 'accepted';
};

const DEFAULT_COUNTRY_CODE = '30';

function normalizePhone(raw: string): string | null {
  let digits = raw.replace(/[^\d+]/g, '');
  if (digits.startsWith('00')) digits = `+${digits.slice(2)}`;
  if (digits.startsWith('+')) {
    return digits;
  }
  digits = digits.replace(/^0+/, '');
  if (!digits) return null;
  return `+${DEFAULT_COUNTRY_CODE}${digits}`;
}

/** Δυναμικό import — αν το native module λείπει (π.χ. παλαιότερο build χωρίς expo-contacts), δεν κρασάρει το app. */
async function loadContactsModule(): Promise<typeof import('expo-contacts') | null> {
  try {
    return await import('expo-contacts');
  } catch {
    return null;
  }
}

export async function getContactsPermissionStatus(): Promise<string> {
  const Contacts = await loadContactsModule();
  if (!Contacts) return 'undetermined';
  try {
    const { status } = await Contacts.getPermissionsAsync();
    return status;
  } catch {
    return 'undetermined';
  }
}

export async function requestContactsPermission(): Promise<boolean> {
  const Contacts = await loadContactsModule();
  if (!Contacts) return false;
  const { status } = await Contacts.requestPermissionsAsync();
  return status === Contacts.PermissionStatus.GRANTED;
}

async function getNormalizedDeviceContactPhones(): Promise<string[]> {
  const Contacts = await loadContactsModule();
  if (!Contacts) return [];
  const { data } = await Contacts.getContactsAsync({
    fields: [Contacts.Fields.PhoneNumbers],
  });
  const phones = new Set<string>();
  for (const contact of data) {
    for (const p of contact.phoneNumbers ?? []) {
      if (!p.number) continue;
      const normalized = normalizePhone(p.number);
      if (normalized) phones.add(normalized);
    }
  }
  return Array.from(phones);
}

export async function findContactsOnHeirapsia(): Promise<MatchedContact[]> {
  const phones = await getNormalizedDeviceContactPhones();
  if (phones.length === 0) return [];

  const { data, error } = await supabase.rpc('match_contacts_by_phone' as never, { p_phones: phones } as never);
  if (error) throw error;
  return (data ?? []) as MatchedContact[];
}
